// Capacitor Local Notifications for iOS / Android habit reminders.
//
// WHY THIS EXISTS
// The original `reminders.js` used `Notification` + Web Push (VAPID). That
// works in mobile Safari PWA but NOT in iOS native (WKWebView doesn't
// expose the browser Push API; APNs requires extra entitlements + server
// infra). So in the TestFlight binary the app never even appeared in iOS
// Settings → Notifications.
//
// Local Notifications solve this:
//   • No APNs / push token / server round-trip needed.
//   • Scheduled with iOS-native `on: { hour, minute, weekday, repeats: true }`
//     so they fire even when the app is closed.
//   • Show up in iOS Settings → Notifications → My Flow Habit immediately
//     after the first `requestPermissions()` call.
//
// USAGE
//   await initLocalNotifications();        // once, on app start
//   await rescheduleHabitReminders(userId); // after habit CRUD or login

import { Capacitor } from '@capacitor/core';
import { supabase } from './supabase';

const isNative = () => {
  try { return Capacitor?.isNativePlatform?.() === true; } catch { return false; }
};
const platformIsIOS = () => {
  try { return Capacitor?.getPlatform?.() === 'ios'; } catch { return false; }
};

// iOS allows max 64 pending local notifications per app — we cap below.
const MAX_PENDING = 60;

// Map our schedule_type → array of weekday numbers (Capacitor: Sun=1..Sat=7).
const weekdaysFor = (schedule_type, custom = []) => {
  switch (schedule_type) {
    case 'daily':    return null; // null = no weekday filter, fires every day
    case 'weekdays': return [2, 3, 4, 5, 6]; // Mon-Fri (Cap: Sun=1)
    case 'weekends': return [1, 7];          // Sun, Sat
    case 'custom':   // our app stores Sun=0..Sat=6 — convert to Cap (Sun=1..Sat=7)
      return (custom || []).map((d) => d + 1);
    default:         return null;
  }
};

const habitBody = (h) =>
  h.type === 'binary'
    ? 'Time for your habit.'
    : h.type === 'time'
    ? `Time to focus for ${h.target_value} min.`
    : `Aim for ${h.target_value}${h.unit ? ' ' + h.unit : ''} today.`;

// Stable 32-bit-safe ID derived from habit.id + weekday so we can cancel and
// re-schedule deterministically without leaking pending notifications.
const idFor = (habitId, weekday) => {
  let h = 5381;
  const s = `${habitId}:${weekday ?? 'd'}`;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  }
  return h % 2147483647;
};

/**
 * Returns the raw `display` value from the plugin:
 *   'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | undefined
 * plus our own derived booleans for convenience.
 */
export const localNotificationsStatus = async () => {
  if (!isNative()) return { supported: false, granted: false, display: null };
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  try {
    const res = await LocalNotifications.checkPermissions();
    const display = res?.display || null;
    return {
      supported: true,
      granted: display === 'granted',
      denied: display === 'denied',
      canPrompt: display === 'prompt' || display === 'prompt-with-rationale',
      display,
    };
  } catch (err) {
    return { supported: true, granted: false, denied: false, canPrompt: false, display: null, error: err?.message };
  }
};

/**
 * Request notification permission. If iOS has already denied previously,
 * this returns the cached denied state WITHOUT showing the prompt — the
 * caller should then offer "Open iOS Settings".
 */
export const requestLocalNotificationPermission = async () => {
  if (!isNative()) return { supported: false, granted: false, display: null };
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  try {
    const res = await LocalNotifications.requestPermissions();
    const display = res?.display || null;
    return {
      supported: true,
      granted: display === 'granted',
      denied: display === 'denied',
      display,
    };
  } catch (err) {
    return { supported: true, granted: false, denied: false, display: null, error: err?.message };
  }
};

/**
 * Open the OS-level Settings page for this app so the user can flip the
 * notification toggle manually. On iOS this is `app-settings:` (the iOS
 * sandbox URL scheme) which Apple guarantees opens directly to OUR app's
 * Settings sub-page. No-op on web.
 */
export const openOSSettingsForNotifications = async () => {
  if (!isNative()) return false;
  try {
    const { App } = await import('@capacitor/app');
    if (platformIsIOS()) {
      await App.openUrl({ url: 'app-settings:' });
    } else {
      // Android: there's no universal "open my app settings" URL via
      // @capacitor/app. Best-effort: a Settings intent. If unavailable,
      // fall back to a no-op so callers can surface a message.
      try { await App.openUrl({ url: 'app-settings:' }); } catch { /* no-op */ }
    }
    return true;
  } catch {
    return false;
  }
};

/**
 * Cancel all currently-pending notifications scheduled by this app, then
 * re-schedule one entry per (habit × scheduled weekday). Safe to call
 * repeatedly; iOS dedupes by notification ID.
 *
 * userId is optional — if omitted, we look up the current session. This
 * lets callers (HabitReminderDialog, HabitFormModal) trigger a reschedule
 * without having to plumb the userId through.
 */
export const rescheduleHabitReminders = async (userId) => {
  if (!isNative()) return { scheduled: 0, skipped: 'not-native' };
  if (!userId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return { scheduled: 0, skipped: 'no-user' };
    } catch {
      return { scheduled: 0, skipped: 'no-user' };
    }
  }
  const { LocalNotifications } = await import('@capacitor/local-notifications');

  // Skip silently if user hasn't granted permission yet — the caller is
  // responsible for prompting first.
  const status = await LocalNotifications.checkPermissions();
  if (status.display !== 'granted') {
    return { scheduled: 0, skipped: 'no-permission', display: status.display };
  }

  // 1) Cancel everything we previously scheduled.
  try {
    const pending = await LocalNotifications.getPending();
    if (pending.notifications?.length) {
      await LocalNotifications.cancel({
        notifications: pending.notifications.map((n) => ({ id: n.id })),
      });
    }
  } catch { /* no-op */ }

  // 2) Pull habits with a reminder set.
  const { data: habits, error } = await supabase
    .from('habits')
    .select('id,name,type,target_value,unit,schedule_type,schedule_days,reminder_time')
    .is('archived_at', null)
    .not('reminder_time', 'is', null);
  if (error || !habits || habits.length === 0) {
    return { scheduled: 0 };
  }

  // 3) Build notifications array.
  const notifications = [];
  for (const h of habits) {
    if (!h.reminder_time) continue;
    const [hh, mm] = h.reminder_time.split(':').map((s) => parseInt(s, 10));
    if (Number.isNaN(hh) || Number.isNaN(mm)) continue;

    const weekdays = weekdaysFor(h.schedule_type, h.schedule_days);
    const title = `${h.name} · My Flow Habit`;
    const body = habitBody(h);

    if (weekdays === null) {
      notifications.push({
        id: idFor(h.id, null),
        title, body,
        schedule: { on: { hour: hh, minute: mm }, allowWhileIdle: true, repeats: true },
        actionTypeId: 'HABIT_REMINDER',
        extra: { habitId: h.id, url: '/today' },
      });
    } else {
      for (const wd of weekdays) {
        notifications.push({
          id: idFor(h.id, wd),
          title, body,
          schedule: { on: { weekday: wd, hour: hh, minute: mm }, allowWhileIdle: true, repeats: true },
          actionTypeId: 'HABIT_REMINDER',
          extra: { habitId: h.id, url: '/today' },
        });
      }
    }
    if (notifications.length >= MAX_PENDING) break;
  }

  if (notifications.length === 0) return { scheduled: 0 };

  await LocalNotifications.schedule({ notifications });
  return { scheduled: notifications.length };
};

/**
 * Register the "HABIT_REMINDER" action category so iOS notifications show
 * the Mark Done + Snooze 1h buttons. Must be called once at app start
 * (before scheduling any notifications). Safe to call repeatedly.
 */
export const registerHabitNotificationActions = async () => {
  if (!isNative()) return;
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  try {
    await LocalNotifications.registerActionTypes({
      types: [
        {
          id: 'HABIT_REMINDER',
          actions: [
            { id: 'mark-done',  title: 'Mark done',  destructive: false, foreground: false },
            { id: 'snooze-1h',  title: 'Snooze 1h',  destructive: false, foreground: false },
          ],
        },
      ],
    });
  } catch { /* no-op */ }
};

/**
 * Queue of mark-done actions that fired while the app was backgrounded /
 * not yet authenticated. AuthContext drains this once a user session is
 * available and writes the habit logs.
 */
const PENDING_KEY = 'mfh.pending-habit-actions';

export const queuePendingAction = (action) => {
  try {
    const existing = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
    existing.push({ ...action, at: new Date().toISOString() });
    localStorage.setItem(PENDING_KEY, JSON.stringify(existing));
  } catch { /* no-op */ }
};

export const drainPendingActions = async () => {
  try {
    const queue = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
    if (!queue.length) return 0;
    // Don't drain until we have a session — otherwise RLS will reject every
    // insert and we'll silently lose the action. The caller (AuthContext)
    // only invokes this AFTER session restore, but double-check anyway.
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) return 0;
    localStorage.removeItem(PENDING_KEY);
    let done = 0;
    for (const item of queue) {
      if (item.actionId === 'mark-done' && item.habitId) {
        const today = new Date().toISOString().slice(0, 10);
        const { data: habit } = await supabase
          .from('habits')
          .select('type,target_value')
          .eq('id', item.habitId)
          .maybeSingle();
        const payload = {
          user_id: user.id,            // required by RLS policy `auth.uid() = user_id`
          habit_id: item.habitId,
          log_date: today,
          status: 'done',
          value: habit?.type === 'binary' ? 1 : Number(habit?.target_value || 1),
        };
        const { error } = await supabase
          .from('habit_logs')
          .upsert(payload, { onConflict: 'habit_id,log_date' });
        if (error) {
          // Re-queue so we can retry on next launch.
          queuePendingAction(item);
          console.warn('[mfh] mark-done failed:', error.message);
        } else {
          done++;
          // Tell open pages (Today) to re-fetch so the habit flips to done
          // immediately when the user opens / is on Today.
          try { window.dispatchEvent(new CustomEvent('mfh:habit-logged', { detail: { habitId: item.habitId } })); } catch { /* no-op */ }
        }
      }
    }
    return done;
  } catch (e) {
    console.warn('[mfh] drainPendingActions error:', e?.message);
    return 0;
  }
};

/**
 * Schedule a one-off snooze notification 1 hour from now for the given habit.
 * Does not modify the recurring daily schedule.
 */
export const snoozeHabitOneHour = async (habitId) => {
  if (!isNative() || !habitId) return false;
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  try {
    const { data: habit } = await supabase
      .from('habits')
      .select('id,name,type,target_value,unit')
      .eq('id', habitId)
      .maybeSingle();
    if (!habit) return false;
    const at = new Date(Date.now() + 60 * 60 * 1000);
    await LocalNotifications.schedule({
      notifications: [{
        id: idFor(habit.id, 'snooze'),
        title: `${habit.name} · My Flow Habit`,
        body: habitBody(habit),
        schedule: { at, allowWhileIdle: true },
        actionTypeId: 'HABIT_REMINDER',
        extra: { habitId: habit.id, url: '/today' },
      }],
    });
    return true;
  } catch {
    return false;
  }
};


/**
 * Wire a tap handler so when the user taps a habit reminder OR one of its
 * action buttons, the handler fires with the action object. Returns an
 * unsubscribe fn.
 */
export const onLocalNotificationTapped = async (handler) => {
  if (!isNative()) return () => {};
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  try {
    const sub = await LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
      handler(action);
    });
    return () => { try { sub.remove(); } catch { /* no-op */ } };
  } catch {
    return () => {};
  }
};
